import { RuleContractLanguageAccordion } from "components/ContractLanguageSection/RuleContractLanguageAccordion";
import {
  IRuleAttachment,
  RuleType,
} from "components/ContractLanguageSection/types";
import { ToastContext } from "context/ToastContext";
import { formatDateOnlyForDisplay } from "helpers/date";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableExpandedRows } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Menu } from "primereact/menu";
import { Tag } from "primereact/tag";
import { ReactNode, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToggleRuleEnabledMutation } from "store/queries/adjustmentRules";
import {
  useCopyContractRulesFromMutation,
  useGetContractFeeSchedulesQuery,
} from "store/queries/contractFeeSchedules";
import { useDeleteExplodingFeeScheduleMutation } from "store/queries/explodingFeeSchedule";
import { useDeleteInpatientRulesetMutation } from "store/queries/inpatientRulesets";
import { useGetInstitutionalRulesetForContractQuery } from "store/queries/institutionalRuleset";
import { useDeletePackageRulesetMutation } from "store/queries/packageRuleset";
import { useDeleteCaseRateRulesetMutation } from "store/queries/pricing";
import { useDeleteDiscountRulesetMutation } from "store/queries/procedureDiscountRuleset";
import { useGetProfessionalRulesetForContractQuery } from "store/queries/professionalRuleset";
import styled from "styled-components";
import type { IRuleToggleType } from "types/AdjustmentRules/AdjustmentRules";
import type { RuleInheritedFrom } from "types/DefaultRules/DefaultRules";

import AddFromDefaultButton from "../../components/AddFromDefaultButton";

const formatCentsToDollars = (cents: number | null | undefined) =>
  cents != null
    ? (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })
    : "—";

// INPATIENT_OUTLIER enum → human label (mirrors ContractView).
const IPO_TRIGGER_LABEL: Record<string, string> = {
  STANDARD_CHARGE_THRESHOLD: "Charge Threshold",
  MODIFIED_CHARGE_THRESHOLD: "Modified Charge Threshold",
  LOS_RANGE: "LOS Range",
  PER_CODE_CHARGE_THRESHOLD: "Per-Code Charge Threshold",
  DISCHARGE_STATUS: "Discharge Status",
  ALWAYS: "Always",
};
const IPO_FORMULA_LABEL: Record<string, string> = {
  PER_DIEM_X_LOS: "Per Diem × LOS",
  PER_DIEM_X_EXCESS_LOS: "Per Diem × Excess LOS",
  FLAT_RATE: "Flat Rate",
  PERCENT_OF_CHARGES: "Percent of Charges",
  PERCENT_OF_EXCESS_CHARGES: "Percent of Excess Charges",
  CASE_RATE_PLUS_PER_DIEM: "Case Rate + Per Diem",
};
const IPO_MODE_LABEL: Record<string, string> = {
  REPLACE: "Replace base",
  ADDITIVE: "Add to base",
  MAX: "Max vs base",
};

const TRANSFER_LOS_FORMULA_LABEL: Record<string, string> = {
  LOS_MINUS_1: "Per Diem × (LOS - 1)",
  LOS: "Per Diem × LOS",
  LOS_PLUS_1: "Per Diem × (LOS + 1)",
};
const TRANSFER_LOS_FORMULA_SHORT: Record<string, string> = {
  LOS_MINUS_1: "(LOS - 1)",
  LOS: "LOS",
  LOS_PLUS_1: "(LOS + 1)",
};

// Per-type config detail for inpatient rulesets — mirrors the per-type blocks
// in ContractView so no information is lost in the unified table. DRG codes are
// omitted here because they have their own column.
const renderInpatientDetails = (ruleset: any): ReactNode => {
  const { config } = ruleset;
  if (!config) return null;

  switch (ruleset.type) {
    case "FIXED_COST":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Fixed Cost</div>
          <div className="text-900">
            CCR: {config.costToChargeRatio?.toFixed(4)}, Threshold: $
            {formatCentsToDollars(config.fixedLossThresholdInCents)}, Outlier:{" "}
            {config.outlierPercent != null
              ? `${config.outlierPercent}%`
              : "80%"}
          </div>
          <div className="text-500 text-xs mt-1">
            Outlier = {config.outlierPercent ?? 80}% × (Charges × CCR − DRG Rate
            − Threshold)
          </div>
        </div>
      );
    case "PASS_THROUGH":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Pass Through</div>
          <div className="text-900">
            Threshold: ${formatCentsToDollars(config.thresholdInCents)},
            Multiplier:{" "}
            {config.chargeMultiplier != null
              ? config.chargeMultiplier.toFixed(4)
              : "—"}
          </div>
          <div className="text-500 text-xs mt-1">
            Expected = Charges ×{" "}
            {config.chargeMultiplier?.toFixed(2) ?? "Multiplier"} (when charges
            exceed threshold)
          </div>
        </div>
      );
    case "EXCESSIVE_LOS":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Excessive LOS</div>
          <div className="text-900">Formula: {config.formula || "—"}</div>
          <div className="text-500 text-xs mt-1">
            Expected = DRG Rate + (Per Diem × Outlier Days). Params come from
            ProviderProcedure DRG fields.
          </div>
        </div>
      );
    case "TRANSFER":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Transfer</div>
          <div className="text-900">
            LOS Formula:{" "}
            {TRANSFER_LOS_FORMULA_LABEL[config.losFormula as string] ||
              "Per Diem × LOS"}
            , Discharge Codes: {config.dischargeStatusCodes?.join(", ") || "—"}
          </div>
          <div className="text-500 text-xs mt-1">
            Expected = min(DRG Rate, Per Diem ×{" "}
            {TRANSFER_LOS_FORMULA_SHORT[config.losFormula as string] || "LOS"})
            when discharge status matches
          </div>
        </div>
      );
    case "STOP_LOSS":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Stop Loss</div>
          <div className="text-900">
            Threshold: {config.thresholdMultiplier}% of DRG, Payment:{" "}
            {config.paymentPercent}% of charges
          </div>
          <div className="text-500 text-xs mt-1">
            When charges &gt; DRG Rate × {config.thresholdMultiplier}%, Expected
            = {config.paymentPercent}% × Total Charges
          </div>
        </div>
      );
    case "CHARGE_OUTLIER":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Charge Outlier</div>
          <div className="text-900">
            {config.thresholdType === "FLAT"
              ? `Threshold: $${formatCentsToDollars(
                  config.thresholdAmountInCents
                )}`
              : `Threshold: ${config.thresholdMultiplier}× base payment`}
            , Outlier: {config.outlierPercent}% of excess
            {config.chargeFloorInCents != null &&
              `, Floor: $${formatCentsToDollars(config.chargeFloorInCents)}`}
          </div>
          <div className="text-500 text-xs mt-1">
            Outlier = {config.outlierPercent}% × (Charges −{" "}
            {config.thresholdType === "FLAT"
              ? "Threshold"
              : `${config.thresholdMultiplier}× Base`}
            )
          </div>
        </div>
      );
    case "INPATIENT_OUTLIER":
      return (
        <div>
          <div className="text-600 text-sm mb-1">Inpatient Outlier Rule</div>
          <div className="text-900">
            Trigger:{" "}
            {IPO_TRIGGER_LABEL[config.trigger?.type] ?? config.trigger?.type},
            Formula:{" "}
            {IPO_FORMULA_LABEL[config.formula?.type] ?? config.formula?.type},
            Mode: {IPO_MODE_LABEL[config.mode] ?? config.mode}
          </div>
          <div className="text-500 text-xs mt-1">
            {config.mode === "REPLACE" &&
              "Replaces base expected when triggered (overrides Transfer too)"}
            {config.mode === "ADDITIVE" &&
              "Added to base expected when triggered"}
            {config.mode === "MAX" &&
              "Competes via Math.max() with base expected"}
          </div>
        </div>
      );
    default:
      return null;
  }
};

// Total / per-code rate detail for exploding fee schedule rulesets — mirrors
// the per-section blocks in ContractView so no information is lost.
const renderExplodingFeeDetails = (ruleset: any): ReactNode => {
  const totalRate = (ruleset.totalRateInCents ?? 0) / 100;
  const codeCount = (ruleset.cptCodes ?? []).length;
  const perCodeRate = codeCount > 0 ? totalRate / codeCount : null;
  return (
    <div>
      <div className="text-900">
        Total Rate: $
        {totalRate.toLocaleString("en-US", { minimumFractionDigits: 2 })}
      </div>
      <div className="text-900">
        Per-Code Rate:{" "}
        {perCodeRate != null
          ? `$${perCodeRate.toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })}`
          : "—"}
      </div>
    </div>
  );
};

const summarizeCodes = (codes: string[]): string => {
  if (!codes.length) return "—";
  const extra = codes.length - 5;
  return codes.slice(0, 5).join(", ") + (extra > 0 ? ` + ${extra} others` : "");
};

// Bundled-code detail for package rulesets — the trigger codes go in the
// CPT/DRG column; bundled codes are shown here so no information is lost.
const renderPackageDetails = (ruleset: any): ReactNode => (
  <div>
    <div className="text-600 text-sm mb-1">Bundled CPT Codes</div>
    <div className="text-900">
      {summarizeCodes((ruleset.bundledCptCodes ?? []) as string[])}
    </div>
  </div>
);

// CLE-2397 — MPDR primary-selection method labels (mirrors PayerRulesTable).
const PRIMARY_SELECTION_METHOD_LABELS: Record<string, string> = {
  ENDO_FAMILY_RVU: "Endo family RVU",
  RVU: "RVU",
  CONTRACTED_DOLLARS: "Contracted dollars",
  BILATERAL_RVU_ADJUSTED: "Bilateral-adjusted RVU",
};

// Professional ruleset config detail — mirrors the Additional Info column on
// the /payer-rules page. Falls back to the same defaults as the standalone
// card when no ruleset record exists for this contract.
const renderProfessionalDetails = (ruleset: any): ReactNode => {
  const primaryMethodLabel =
    PRIMARY_SELECTION_METHOD_LABELS[
      ruleset?.primarySelectionMethod ?? "ENDO_FAMILY_RVU"
    ] ?? "Endo family RVU";
  const compoundBilateralAndMppr = ruleset?.compoundBilateralAndMppr ?? true;
  const endoApplies = ruleset?.endoApplies ?? true;
  const disabledFamilyCount = ruleset?.disabledEndoBaseCptCodes?.length ?? 0;
  const disabledFamilyNote =
    endoApplies && disabledFamilyCount > 0
      ? ` (${disabledFamilyCount} ${
          disabledFamilyCount === 1 ? "family" : "families"
        } turned off)`
      : "";
  return (
    <div className="flex flex-column gap-1">
      <span className="text-900">Primary: {primaryMethodLabel}</span>
      <span className="text-900">
        Compound Bilateral + MPPR: {compoundBilateralAndMppr ? "ON" : "OFF"}
      </span>
      <span className="text-900">
        Endoscopy Rules: {endoApplies ? "ON" : "OFF"}
        {disabledFamilyNote}
      </span>
      {/* CLE-2597 — same reasoning as renderInstitutionalDetails: say when these
          values are engine defaults rather than a stored rule. */}
      {!ruleset && (
        <span className="text-600 text-sm">
          Engine default — no rule stored for this provider + payer.
        </span>
      )}
    </div>
  );
};

// One "Add from default" entry per ruleset type — supplies the list/copy hooks
// to the reused AddFromDefaultButton dialog.
interface IAddFromDefaultOption {
  label: string;
  entityLabel: string;
  useList: any;
  useCopy: any;
}

interface IAdjustmentRulesProps {
  mpdrRulesets?: any[];
  caseRateRulesets?: any[];
  inpatientRulesets?: any[];
  explodingFeeScheduleRulesets?: any[];
  packageRulesets?: any[];
  // Hospital staff can't reach the payer-rules edit pages, so names aren't linked.
  hospitalId?: string | null;
  // "Add from default" support — omitted for hospital staff / unsaved contracts.
  addFromDefaultOptions?: IAddFromDefaultOption[];
  providerId?: string;
  payerId?: number;
  // CLE-2264 — enables "Copy rules from…" (bulk copy from another contract's
  // pair). Omitted for hospital staff, same as addFromDefaultOptions.
  contractId?: string;
  onRuleAdded?: () => void;
  // Whether the Professional ruleset row applies (hidden for facility/anesthesia).
  showProfessional?: boolean;
  // Whether the Institutional ruleset row applies (facility only — the complement of
  // showProfessional over Provider.specialty).
  showInstitutional?: boolean;
  // Provider/payer names for the Professional row when no ruleset record exists.
  providerName?: string;
  payerName?: string;
}

// Inpatient category grouping (mirrors ContractView's getCategory).
// Institutional (UB-04 / FACILITY) ruleset detail — the complement of
// renderProfessionalDetails over Provider.specialty. Falls back to the engine default
// (contracted dollars) when no ruleset record exists for this contract, which is the
// common case: the row exists to be the exception, not the norm.
const renderInstitutionalDetails = (ruleset: any): ReactNode => {
  const primaryMethodLabel =
    PRIMARY_SELECTION_METHOD_LABELS[
      ruleset?.primarySelectionMethod ?? "CONTRACTED_DOLLARS"
    ] ?? "Contracted dollars";
  return (
    <div className="flex flex-column gap-1">
      <span className="text-900">Primary: {primaryMethodLabel}</span>
      {/* CLE-2597 — without this, a stored CONTRACTED_DOLLARS rule and the absence
          of any rule render identically, which is what sent people hunting for a
          nonexistent row in the /payer-rules list. */}
      {!ruleset && (
        <span className="text-600 text-sm">
          Engine default — no rule stored for this provider + payer.
        </span>
      )}
    </div>
  );
};

const INPATIENT_CATEGORY_TITLES: Record<string, string> = {
  OVERRIDE: "Overrides",
  ADJUSTMENT: "Adjustments",
  SAFETY_NET: "Safety Nets",
};

const INPATIENT_CATEGORY_DESCRIPTIONS: Record<string, string> = {
  OVERRIDE: "Replace the base expected amount entirely",
  ADJUSTMENT: "Added to the base expected amount",
  SAFETY_NET: "Compete with the base via Math.max()",
};

const getInpatientCategory = (ruleset: any): string => {
  if (ruleset.type === "TRANSFER" || ruleset.type === "EXCESSIVE_LOS")
    return "OVERRIDE";
  if (ruleset.type === "STOP_LOSS") return "SAFETY_NET";
  if (ruleset.type === "INPATIENT_OUTLIER") {
    const mode = ruleset.config?.mode;
    if (mode === "REPLACE") return "OVERRIDE";
    if (mode === "MAX") return "SAFETY_NET";
    return "ADJUSTMENT";
  }
  return "ADJUSTMENT";
};

interface IDiscount {
  label: string;
  value: number | null | undefined;
}

interface IAdditionalInfo {
  text: string;
  icon?: boolean;
}

// The five copyable rule types can be deleted straight from the contract view;
// each maps to its own per-type delete endpoint. Professional/Institutional
// rulesets are deliberately excluded — they are unique per (provider, payer).
type TDeletableRuleType =
  | "MPPR"
  | "CASE_RATE"
  | "PACKAGE"
  | "EXPLODING_FEE"
  | "INPATIENT";

interface IAdjustmentRule {
  id: string;
  rowKey: string;
  ruleType: string;
  ruleTypeSubLabel?: string;
  ruleTypeDescription?: string;
  ruleName: string;
  providerName: string;
  payerName: string;
  // CLE-2547 parity — null/undefined means the rule is live; a timestamp means
  // it applies to neither estimates nor claims.
  disabledAt?: string | null;
  // Key the shared enable/disable endpoint expects; absent on rows that can't
  // be toggled (e.g. synthetic Professional/Institutional default rows).
  toggleType?: IRuleToggleType;
  // Present only on rows that support per-row deletion.
  deleteType?: TDeletableRuleType;
  // CLE-2597 — true on the synthetic Professional/Institutional rows that stand in
  // for engine defaults when no DB record exists. Such a row is real behavior but
  // not a rule: it has no id to toggle, edit or delete, and it will never appear in
  // the /payer-rules list, which only lists stored rows.
  isEngineDefault?: boolean;
  // CLE-2597 — set when this row's values come from a fallback rather than the contract's own
  // rule; links straight to that fallback so it can be inspected or edited from here.
  inheritedFromPath?: string;
  // CLE-2842 step 2 — set on an MPPR / Case Rate / Package / Exploding Fee / Inpatient rule that
  // reaches this contract from a less specific rung (this provider on All payers, this payer's
  // fallback, or the global fallback). It is not this contract's rule: no switch, no delete —
  // its name opens the rule itself.
  inheritedFrom?: RuleInheritedFrom;
  discounts: IDiscount[];
  cptCodes: string[];
  excludedCptCodes: string[];
  additionalInfo: IAdditionalInfo[];
  details?: ReactNode;
  editPath: string;
  // Contract-language accordion content (shown in the expanded row).
  accordionRuleType: RuleType;
  contractLanguage?: string | null;
  attachments: IRuleAttachment[];
  hasLanguageContent: boolean;
}

// Rule-type tag styling mirrors the /payer-rules table (PayerRulesTable.tsx).
const RULE_TYPE_TAG_PALETTE: Record<
  string,
  { $bg: string; $fg: string; $border: string }
> = {
  MPPR: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  "Case Rate": {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
  Inpatient: {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-300)",
  },
  "Exploding Fee Schedule": {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-300)",
  },
  Package: {
    $bg: "var(--primary-100)",
    $fg: "var(--primary-800)",
    $border: "var(--primary-200)",
  },
  Professional: {
    $bg: "var(--primary-50)",
    $fg: "var(--primary-700)",
    $border: "var(--primary-200)",
  },
  // Teal (not the --primary- ramp every other type uses) so Institutional is distinguishable from
  // Professional in the global /payer-rules list, where both appear side by side. Matches the badge
  // in PayerRulesTable; the vars are defined in layout/styles/clear-theme-overrides.css.
  Institutional: {
    $bg: "var(--teal-50)",
    $fg: "var(--teal-700)",
    $border: "var(--teal-200)",
  },
};

const DEFAULT_TAG_PALETTE = {
  $bg: "var(--primary-50)",
  $fg: "var(--primary-800)",
  $border: "var(--primary-300)",
};

const StyledRuleTypeTag = styled(Tag)<{
  $bg: string;
  $fg: string;
  $border: string;
}>`
  &.p-tag.p-component {
    background: ${(p) => p.$bg} !important;
    color: ${(p) => p.$fg} !important;
    border: 1px solid ${(p) => p.$border} !important;
    font-weight: 600;
    font-size: 0.75rem;
    width: fit-content;
    border-radius: 8px;
  }
`;

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  /* CLE-2547 parity — a disabled rule stays listed and editable, but reads as inactive at a
     glance. The status-switch cell is excluded (by class, since the hospital view drops the
     status column and would shift any nth-child index) so it keeps full contrast and stays
     obviously clickable. */
  .p-datatable-tbody
    > tr.adjustment-rule-row--disabled
    > td:not(.rule-status-cell) {
    opacity: 0.45;
  }
`;

// CLE-2842 step 2 — the tag an inherited rule carries on the contract page.
export const INHERITED_FROM_LABELS: Record<RuleInheritedFrom, string> = {
  PROVIDER_ALL_PAYERS: "Inherited — all payers",
  PAYER_FALLBACK: "Inherited — payer fallback",
  GLOBAL_FALLBACK: "Inherited — global fallback",
};

/**
 * The scope columns and actions of one of the five hydrated rule types. A contract's own rule
 * keeps today's values; an inherited one names its real reach and drops the inline actions (they
 * would act on a rule other contracts share). The detail lists inherited rules after own ones.
 */
const additiveRuleScope = <TType extends IRuleToggleType & TDeletableRuleType>(
  ruleset: any,
  type: TType
) => {
  const inheritedFrom: RuleInheritedFrom | undefined =
    ruleset.inheritedFrom ?? undefined;
  if (!inheritedFrom) {
    return {
      providerName: ruleset.provider?.name ?? "N/A",
      payerName: ruleset.payer?.name ?? "N/A",
      toggleType: type,
      deleteType: type,
    };
  }
  return {
    inheritedFrom,
    providerName:
      inheritedFrom === "PROVIDER_ALL_PAYERS"
        ? ruleset.provider?.name ?? "N/A"
        : "All providers",
    payerName: ruleset.payer?.name ?? "All payers",
    toggleType: undefined,
    deleteType: undefined,
  };
};

/**
 * Unified "Adjustment Rules" section for the contract view. Will replace the
 * separate per-type ruleset cards (MPPR, Case Rate, Inpatient, etc.), listing
 * each rule with its type and name like the Adjustment page.
 */
export default function AdjustmentRules({
  mpdrRulesets,
  caseRateRulesets,
  inpatientRulesets,
  explodingFeeScheduleRulesets,
  packageRulesets,
  hospitalId,
  addFromDefaultOptions,
  providerId,
  payerId,
  contractId,
  onRuleAdded,
  showProfessional,
  showInstitutional,
  providerName,
  payerName,
}: IAdjustmentRulesProps) {
  const navigate = useNavigate();

  // Professional ruleset is fetched per (provider, payer); rendered as one row.
  const { data: professionalRulesetData, isLoading: isProfessionalLoading } =
    useGetProfessionalRulesetForContractQuery(
      { providerId: providerId as string, payerId: payerId as number },
      { skip: !showProfessional || !providerId || payerId == null }
    );
  const professionalRuleset = professionalRulesetData?.data ?? null;
  // Institutional ruleset is fetched per (provider, payer); rendered as one row.
  const { data: institutionalRulesetData, isLoading: isInstitutionalLoading } =
    useGetInstitutionalRulesetForContractQuery(
      { providerId: providerId as string, payerId: payerId as number },
      { skip: !showInstitutional || !providerId || payerId == null }
    );
  const institutionalRuleset = institutionalRulesetData?.data ?? null;
  // CLE-2597 — the lookup now resolves the whole ladder and reports which rung matched, so a
  // contract governed by a fallback shows that fallback's real values instead of "Not
  // configured". Only ENGINE_DEFAULT (nothing stored anywhere) is still unconfigured, and it is
  // only true once the lookup has settled — in flight the ruleset is null too, and claiming
  // "Not configured" on a contract that IS configured is worse than saying nothing for a moment.
  const professionalSource = professionalRulesetData?.source;
  const institutionalSource = institutionalRulesetData?.source;
  const professionalIsEngineDefault =
    !professionalRuleset && !isProfessionalLoading;
  const institutionalIsEngineDefault =
    !institutionalRuleset && !isInstitutionalLoading;
  const isInheritedSource = (source?: string) =>
    source === "PAYER_FALLBACK" || source === "GLOBAL_FALLBACK";
  // Sub-label under the rule-type badge: nothing stored anywhere, inherited from a fallback, or
  // the contract's own rule (no label).
  const scopeSubLabel = (
    isEngineDefault: boolean,
    source?: string
  ): string | undefined => {
    if (isEngineDefault) return "System default";
    if (source === "PAYER_FALLBACK") return "Inherited — payer fallback";
    if (source === "GLOBAL_FALLBACK") return "Inherited — system fallback";
    return undefined;
  };
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const addMenuRef = useRef<Menu>(null);
  const [activeAddOption, setActiveAddOption] =
    useState<IAddFromDefaultOption | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // CLE-2264 — "Copy rules from…" dialog state.
  const { current: toastElement } = useContext(ToastContext);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState<string | null>(null);
  const [copyRulesFrom, { isLoading: isCopyingRules }] =
    useCopyContractRulesFromMutation();
  const { data: contractList, isLoading: isContractListLoading } =
    useGetContractFeeSchedulesQuery({}, { skip: !copyDialogOpen });

  // CLE-2547 parity — per-row enable/disable, mirroring PayerRulesTable.
  const [toggleRuleEnabled] = useToggleRuleEnabledMutation();
  // Rules whose toggle request is still in flight, so a row can't be
  // double-clicked into an ambiguous state while the contract refetches.
  const [togglingIds, setTogglingIds] = useState<string[]>([]);

  // Per-type delete endpoints for the five copyable rule types.
  const [deleteDiscountRuleset] = useDeleteDiscountRulesetMutation();
  const [deleteCaseRateRuleset] = useDeleteCaseRateRulesetMutation();
  const [deletePackageRuleset] = useDeletePackageRulesetMutation();
  const [deleteExplodingFeeSchedule] = useDeleteExplodingFeeScheduleMutation();
  const [deleteInpatientRuleset] = useDeleteInpatientRulesetMutation();

  // Source candidates: every other contract except this one and any contract
  // for the same provider+payer pair (its rules already apply here).
  const copySourceOptions = useMemo(
    () =>
      (contractList ?? [])
        .filter(
          (c) =>
            c.id !== contractId &&
            !(c.providerId === providerId && c.payerId === payerId)
        )
        .map((c) => ({
          label: `${c.provider?.name ?? "Unknown provider"} — ${
            c.payer?.name ?? "Unknown payer"
          } (${formatDateOnlyForDisplay(c.effectiveDate)})`,
          value: c.id,
        })),
    [contractList, contractId, providerId, payerId]
  );

  const onHideCopyDialog = () => {
    setCopyDialogOpen(false);
    setCopySourceId(null);
  };

  const onConfirmCopyRules = async () => {
    if (!contractId || !copySourceId) return;
    try {
      const result = await copyRulesFrom({
        contractId,
        sourceContractId: copySourceId,
      }).unwrap();
      const summary = result?.data;
      if (summary?.samePair) {
        toastElement?.show({
          severity: "info",
          summary: "Nothing to copy",
          detail:
            "The selected contract has the same provider and payer — its rules already apply here.",
        });
      } else {
        const copied = summary?.copied ?? 0;
        const skipped = summary?.skipped ?? 0;
        const skipReasons = (summary?.details ?? [])
          .filter((d) => d.status === "skipped" && d.reason)
          .slice(0, 3)
          .map((d) => `${d.name}: ${d.reason}`);
        toastElement?.show({
          severity: "success",
          summary: `Copied ${copied} adjustment rules${
            skipped ? `, ${skipped} skipped` : ""
          }`,
          detail: skipReasons.length ? skipReasons.join("; ") : undefined,
        });
      }
      onRuleAdded?.();
      onHideCopyDialog();
    } catch (e) {
      const err = e as any;
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          err?.data?.message ??
          "Unable to copy rules from the selected contract.",
      });
    }
  };

  // Contract-language / attachments fields shared by every rule type.
  const accordionFields = (ruleset: any, accordionRuleType: RuleType) => {
    const attachments = (ruleset.attachments ?? []) as IRuleAttachment[];
    return {
      accordionRuleType,
      contractLanguage: ruleset.contractLanguage ?? null,
      attachments,
      hasLanguageContent: !!ruleset.contractLanguage || attachments.length > 0,
    };
  };

  const mpprRules: IAdjustmentRule[] = (mpdrRulesets ?? [])
    .filter(Boolean)
    .map((ruleset) => ({
      id: ruleset.id,
      rowKey: `MPPR:${ruleset.id}`,
      ruleType: "MPPR",
      ruleName: ruleset.groupName ?? "Unnamed MPDR Rule Set",
      discounts: [
        { label: "Rank 2", value: ruleset.discountPercentForRank2 },
        { label: "Rank 3", value: ruleset.discountPercentForRank3 },
        { label: "Rank 4", value: ruleset.discountPercentForRank4 },
        { label: "Rank 5+", value: ruleset.discountPercentForRank5Plus },
      ],
      cptCodes: (ruleset.cptCodes ?? []) as string[],
      excludedCptCodes: [],
      additionalInfo: ruleset.allowOnDuplicateCptCodes
        ? [{ text: "Allows duplicate CPT codes", icon: true }]
        : [],
      editPath: `/payer-rules/mppr/edit/${ruleset.id}`,
      disabledAt: ruleset.disabledAt ?? null,
      ...additiveRuleScope(ruleset, "MPPR" as const),
      ...accordionFields(ruleset, "MPPR"),
    }));

  const caseRateRules: IAdjustmentRule[] = (caseRateRulesets ?? [])
    .filter(Boolean)
    .map((ruleset) => ({
      id: ruleset.id,
      rowKey: `CASE_RATE:${ruleset.id}`,
      ruleType: "Case Rate",
      ruleName: ruleset.groupName ?? "Unnamed Case Rate Rule Set",
      discounts: [],
      cptCodes: (ruleset.cptCodes ?? []) as string[],
      excludedCptCodes: (ruleset.excludedCptCodes ?? []) as string[],
      additionalInfo: [],
      editPath: `/payer-rules/case-rate/edit/${ruleset.id}`,
      disabledAt: ruleset.disabledAt ?? null,
      ...additiveRuleScope(ruleset, "CASE_RATE" as const),
      ...accordionFields(ruleset, "CASE_RATE"),
    }));

  const inpatientRules: IAdjustmentRule[] = (inpatientRulesets ?? [])
    .filter(Boolean)
    .map((ruleset) => {
      const category = getInpatientCategory(ruleset);
      return {
        id: ruleset.id,
        rowKey: `INPATIENT:${ruleset.id}`,
        ruleType: "Inpatient",
        ruleTypeSubLabel: INPATIENT_CATEGORY_TITLES[category],
        ruleTypeDescription: INPATIENT_CATEGORY_DESCRIPTIONS[category],
        ruleName: ruleset.name ?? "Unnamed Inpatient Ruleset",
        discounts: [],
        cptCodes: (ruleset.drgCodes ?? []) as string[],
        excludedCptCodes: [],
        additionalInfo: [],
        details: renderInpatientDetails(ruleset),
        editPath: `/payer-rules/inpatient-rulesets/edit/${ruleset.id}`,
        disabledAt: ruleset.disabledAt ?? null,
        ...additiveRuleScope(ruleset, "INPATIENT" as const),
        ...accordionFields(ruleset, "INPATIENT"),
      };
    });

  const explodingFeeRules: IAdjustmentRule[] = (
    explodingFeeScheduleRulesets ?? []
  )
    .filter(Boolean)
    .map((ruleset) => ({
      id: ruleset.id,
      rowKey: `EXPLODING_FEE:${ruleset.id}`,
      ruleType: "Exploding Fee Schedule",
      ruleName: ruleset.groupName ?? "Unnamed Exploding Fee Schedule Ruleset",
      discounts: [],
      cptCodes: (ruleset.cptCodes ?? []) as string[],
      excludedCptCodes: [],
      additionalInfo: [],
      details: renderExplodingFeeDetails(ruleset),
      editPath: `/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`,
      disabledAt: ruleset.disabledAt ?? null,
      ...additiveRuleScope(ruleset, "EXPLODING_FEE" as const),
      ...accordionFields(ruleset, "EXPLODING_FEE"),
    }));

  const packageRules: IAdjustmentRule[] = (packageRulesets ?? [])
    .filter(Boolean)
    .map((ruleset) => ({
      id: ruleset.id,
      rowKey: `PACKAGE:${ruleset.id}`,
      ruleType: "Package",
      ruleName: ruleset.groupName ?? "Unnamed Package Ruleset",
      discounts: [],
      cptCodes: (ruleset.triggerCptCodes ?? []) as string[],
      excludedCptCodes: [],
      additionalInfo: [],
      details: renderPackageDetails(ruleset),
      editPath: `/payer-rules/packages/edit/${ruleset.id}`,
      disabledAt: ruleset.disabledAt ?? null,
      ...additiveRuleScope(ruleset, "PACKAGE" as const),
      ...accordionFields(ruleset, "PACKAGE"),
    }));

  // Always show a Professional row when applicable — like the standalone card,
  // it falls back to engine defaults when no ruleset record exists.
  const professionalRules: IAdjustmentRule[] = showProfessional
    ? [
        {
          id: professionalRuleset?.id ?? "professional-default",
          rowKey: `PROFESSIONAL:${professionalRuleset?.id ?? "default"}`,
          ruleType: "Professional",
          isEngineDefault: professionalIsEngineDefault,
          inheritedFromPath:
            isInheritedSource(professionalSource) && professionalRuleset
              ? `/payer-rules/professional-rulesets/edit/${professionalRuleset.id}`
              : undefined,
          // CLE-2597 — a row governed by a fallback is NOT unconfigured: it shows that
          // fallback's real values, and the sub-label says where they came from.
          ruleTypeSubLabel: scopeSubLabel(
            professionalIsEngineDefault,
            professionalSource
          ),
          ruleName: "—",
          providerName: providerName ?? "N/A",
          payerName: payerName ?? "N/A",
          discounts: [],
          cptCodes: [],
          excludedCptCodes: [],
          additionalInfo: [],
          details: renderProfessionalDetails(professionalRuleset),
          editPath: professionalRuleset
            ? `/payer-rules/professional-rulesets/edit/${professionalRuleset.id}`
            : "",
          disabledAt: professionalRuleset?.disabledAt ?? null,
          // The synthetic "engine defaults" row has no DB record — nothing to
          // toggle (and never anything to delete).
          toggleType: professionalRuleset?.id
            ? ("PROFESSIONAL" as const)
            : undefined,
          // Professional rulesets have no contract-language accordion.
          accordionRuleType: "MPPR",
          contractLanguage: null,
          attachments: [],
          hasLanguageContent: false,
        },
      ]
    : [];

  // Always show an Institutional row when applicable — like Professional, it falls back to
  // the engine default (contracted dollars) when no ruleset record exists.
  const institutionalRules: IAdjustmentRule[] = showInstitutional
    ? [
        {
          id: institutionalRuleset?.id ?? "institutional-default",
          rowKey: `INSTITUTIONAL:${institutionalRuleset?.id ?? "default"}`,
          ruleType: "Institutional",
          isEngineDefault: institutionalIsEngineDefault,
          inheritedFromPath:
            isInheritedSource(institutionalSource) && institutionalRuleset
              ? `/payer-rules/institutional-rulesets/edit/${institutionalRuleset.id}`
              : undefined,
          // CLE-2597 — a row governed by a fallback is NOT unconfigured: it shows that
          // fallback's real values, and the sub-label says where they came from.
          ruleTypeSubLabel: scopeSubLabel(
            institutionalIsEngineDefault,
            institutionalSource
          ),
          ruleName: "—",
          providerName: providerName ?? "N/A",
          payerName: payerName ?? "N/A",
          discounts: [],
          cptCodes: [],
          excludedCptCodes: [],
          additionalInfo: [],
          details: renderInstitutionalDetails(institutionalRuleset),
          editPath: institutionalRuleset
            ? `/payer-rules/institutional-rulesets/edit/${institutionalRuleset.id}`
            : "",
          disabledAt: institutionalRuleset?.disabledAt ?? null,
          // The synthetic "engine defaults" row has no DB record — nothing to
          // toggle (and never anything to delete).
          toggleType: institutionalRuleset?.id
            ? ("INSTITUTIONAL" as const)
            : undefined,
          // Institutional rulesets have no contract-language accordion.
          accordionRuleType: "MPPR",
          contractLanguage: null,
          attachments: [],
          hasLanguageContent: false,
        },
      ]
    : [];

  const rules = [
    ...mpprRules,
    ...caseRateRules,
    ...inpatientRules,
    ...explodingFeeRules,
    ...packageRules,
    ...professionalRules,
    ...institutionalRules,
  ];

  // Professional/Institutional rows have no name ("—"); toasts fall back to the
  // rule type so they never read as `"—" will apply…`.
  function ruleToastLabel(row: IAdjustmentRule) {
    return row.ruleName !== "—"
      ? `"${row.ruleName}"`
      : `This ${row.ruleType} ruleset`;
  }

  async function handleToggleEnabled(row: IAdjustmentRule, enabled: boolean) {
    if (!row.toggleType) return;

    setTogglingIds((ids) => [...ids, row.id]);
    try {
      await toggleRuleEnabled({
        ruleType: row.toggleType,
        ruleId: row.id,
        enabled,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: enabled ? "Rule enabled" : "Rule disabled",
        detail: enabled
          ? `${ruleToastLabel(row)} will apply to new estimates and claims.`
          : `${ruleToastLabel(
              row
            )} will no longer apply to estimates or claims.`,
      });
      onRuleAdded?.();
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: `Could not ${enabled ? "enable" : "disable"} this rule.`,
      });
    } finally {
      setTogglingIds((ids) => ids.filter((id) => id !== row.id));
    }
  }

  function ruleStatusToggle(row: IAdjustmentRule) {
    // Synthetic Professional/Institutional default rows (no DB record) carry no
    // toggleType — nothing exists to enable or disable.
    if (!row.toggleType) return null;
    const enabled = row.disabledAt == null;

    return (
      <InputSwitch
        checked={enabled}
        disabled={togglingIds.includes(row.id)}
        tooltip={
          enabled
            ? "Rule is live — applies to estimates and claims"
            : "Rule is disabled — applies to neither estimates nor claims"
        }
        onChange={(event) => handleToggleEnabled(row, !!event.value)}
      />
    );
  }

  async function handleDeleteRule(row: IAdjustmentRule) {
    const deleteMutationByType: Record<
      TDeletableRuleType,
      (id: string) => { unwrap: () => Promise<any> }
    > = {
      MPPR: deleteDiscountRuleset,
      CASE_RATE: deleteCaseRateRuleset,
      PACKAGE: deletePackageRuleset,
      EXPLODING_FEE: deleteExplodingFeeSchedule,
      INPATIENT: deleteInpatientRuleset,
    };
    if (!row.deleteType) return;

    try {
      await deleteMutationByType[row.deleteType](row.id).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Rule deleted",
        detail: `"${row.ruleName}" has been deleted.`,
      });
      onRuleAdded?.();
    } catch (error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: `Could not delete "${row.ruleName}".`,
      });
    }
  }

  function ruleDeleteButton(row: IAdjustmentRule) {
    // Only the five copyable types are deletable; Professional/Institutional
    // rows (real or synthetic) never render the button.
    if (!row.deleteType) return null;

    return (
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-danger p-button-sm"
        tooltip="Delete rule"
        tooltipOptions={{ position: "left" }}
        onClick={() =>
          confirmDialog({
            header: "Delete rule?",
            icon: "pi pi-exclamation-triangle",
            message: `"${row.ruleName}" will be permanently deleted. This deletes the rule itself — it will stop applying to ${row.providerName} — ${row.payerName} on EVERY contract for this provider + payer, not just this one. This cannot be undone.`,
            acceptLabel: "Delete",
            acceptClassName: "p-button-danger",
            accept: () => handleDeleteRule(row),
          })
        }
      />
    );
  }

  const ruleNameTemplate = (row: IAdjustmentRule) => {
    // CLE-2597 — an engine default is not a stored rule. Saying so here is the
    // whole point: previously it rendered "—" and looked like every other row.
    if (row.isEngineDefault) {
      const createPath =
        row.ruleType === "Institutional"
          ? "/payer-rules/institutional-rulesets/new"
          : "/payer-rules/professional-rulesets/new";
      return (
        <div className="flex flex-column gap-1">
          <span className="text-600 font-italic">Not configured</span>
          {/* Hospital staff are read-only, and without a provider+payer there is
              nothing to prefill the create form with. */}
          {!hospitalId && providerId && payerId != null && (
            <a
              href={createPath}
              onClick={(e) => {
                e.preventDefault();
                navigate(
                  `${createPath}?providerId=${providerId}&payerId=${payerId}`
                );
              }}
              className="text-primary no-underline hover:underline cursor-pointer text-sm"
            >
              Configure
            </a>
          )}
        </div>
      );
    }
    // CLE-2597 — an inherited row has no name of its own, but the fallback governing it is a
    // real rule worth reaching from here.
    if (row.inheritedFromPath && !hospitalId) {
      const target = row.inheritedFromPath;
      return (
        <a
          href={target}
          onClick={(e) => {
            e.preventDefault();
            navigate(target);
          }}
          className="text-primary no-underline hover:underline cursor-pointer text-sm"
        >
          View fallback rule
        </a>
      );
    }
    // Only admins can open the payer-rules edit pages; hospital staff see plain
    // text. The Professional row has no name, so it's never linked.
    if (
      hospitalId ||
      row.ruleType === "Professional" ||
      row.ruleType === "Institutional"
    ) {
      return <span className="text-900">{row.ruleName}</span>;
    }
    return (
      <a
        href={row.editPath}
        onClick={(e) => {
          e.preventDefault();
          navigate(row.editPath);
        }}
        className="text-primary no-underline hover:underline cursor-pointer"
      >
        {row.ruleName}
      </a>
    );
  };

  const ruleTypeTemplate = (row: IAdjustmentRule) => {
    const palette = RULE_TYPE_TAG_PALETTE[row.ruleType] ?? DEFAULT_TAG_PALETTE;
    return (
      <div className="flex flex-column gap-1">
        <StyledRuleTypeTag
          value={row.ruleType}
          $bg={palette.$bg}
          $fg={palette.$fg}
          $border={palette.$border}
        />
        {row.ruleTypeSubLabel && (
          <span className="text-sm text-gray-500">{row.ruleTypeSubLabel}</span>
        )}
        {row.inheritedFrom && (
          <Tag
            value={INHERITED_FROM_LABELS[row.inheritedFrom]}
            severity="info"
            style={{ width: "fit-content" }}
            data-testid="inherited-rule-tag"
          />
        )}
        {row.ruleTypeDescription && (
          <span className="text-xs text-gray-500">
            {row.ruleTypeDescription}
          </span>
        )}
      </div>
    );
  };

  const discountsTemplate = (row: IAdjustmentRule) => {
    if (!row.discounts.length) {
      return <span className="text-600">—</span>;
    }
    return (
      <div className="flex flex-column gap-1">
        {row.discounts.map((d) => (
          <span key={d.label} className="text-900">
            {d.label}: {d.value ?? 0}%
          </span>
        ))}
      </div>
    );
  };

  const codesTemplate = (codes: string[], emptyLabel: string) => {
    if (!codes.length) {
      return <span className="text-600">{emptyLabel}</span>;
    }
    const extra = codes.length - 5;
    return (
      <span className="text-900">
        {codes.slice(0, 5).join(", ")}
        {extra > 0 && <span className="text-600"> + {extra} others</span>}
      </span>
    );
  };

  const additionalInfoTemplate = (row: IAdjustmentRule) => {
    if (row.details) {
      return row.details;
    }
    if (!row.additionalInfo.length) {
      return <span className="text-600">—</span>;
    }
    return (
      <div className="flex flex-column gap-1">
        {row.additionalInfo.map((info) => (
          <span key={info.text} className="text-900">
            {info.icon && (
              <i className="pi pi-check-circle text-green-500 mr-2" />
            )}
            {info.text}
          </span>
        ))}
      </div>
    );
  };

  // Expanded row reveals the contract-language / attachments accordion that the
  // old per-section view rendered under each ruleset.
  const rowExpansionTemplate = (row: IAdjustmentRule) => (
    <div className="px-3 pb-2">
      <RuleContractLanguageAccordion
        ruleType={row.accordionRuleType}
        ruleId={row.id}
        contractLanguage={row.contractLanguage}
        attachments={row.attachments}
      />
    </div>
  );

  const canAddFromDefault =
    !!addFromDefaultOptions?.length && !!providerId && payerId != null;

  const addMenuItems = (addFromDefaultOptions ?? []).map((option) => ({
    label: option.label,
    icon: "pi pi-copy",
    command: () => {
      setActiveAddOption(option);
      setAddDialogOpen(true);
    },
  }));

  // Same gating as "Add from Typical rules" (admin only), plus a saved contract.
  const canCopyRulesFromContract = canAddFromDefault && !!contractId;

  const title = (
    <div className="flex justify-content-between align-items-center">
      <span>Adjustment Rules</span>
      <div className="flex align-items-center gap-2">
        {canCopyRulesFromContract && (
          <Button
            label="Copy rules from…"
            icon="pi pi-clone"
            className="p-button-sm p-button-text"
            onClick={() => setCopyDialogOpen(true)}
          />
        )}
        {canAddFromDefault && (
          <>
            <Button
              label="Add rules"
              icon="pi pi-plus"
              className="p-button-sm p-button-text"
              onClick={(e) => addMenuRef.current?.toggle(e)}
            />
            <Menu model={addMenuItems} popup ref={addMenuRef} />
          </>
        )}
      </div>
    </div>
  );

  return (
    <Card title={title} className="mb-4">
      {/* Reused copy-from-default dialog, driven by the rule-type menu above. */}
      {canAddFromDefault && activeAddOption && (
        <AddFromDefaultButton
          key={activeAddOption.label}
          providerId={providerId as string}
          payerId={payerId as number}
          entityLabel={activeAddOption.entityLabel}
          useList={activeAddOption.useList}
          useCopy={activeAddOption.useCopy}
          onSuccess={onRuleAdded}
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          hideTrigger
        />
      )}
      {/* CLE-2264 — bulk-copy every adjustment rule from another contract's pair. */}
      {canCopyRulesFromContract && (
        <Dialog
          header="Copy rules from another contract"
          visible={copyDialogOpen}
          onHide={onHideCopyDialog}
          style={{ width: "480px", maxWidth: "95vw" }}
          footer={
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <Button
                label="Cancel"
                className="p-button-text"
                onClick={onHideCopyDialog}
                disabled={isCopyingRules}
              />
              <Button
                label="Copy rules"
                icon="pi pi-check"
                onClick={onConfirmCopyRules}
                disabled={!copySourceId || isCopyingRules}
                loading={isCopyingRules}
              />
            </div>
          }
        >
          <p style={{ fontSize: 13, color: "#4b5563", marginTop: 0 }}>
            Copies every adjustment rule from the selected contract&apos;s
            provider + payer to this contract&apos;s provider + payer. Rules
            that conflict with existing ones are skipped and reported.
          </p>
          <Dropdown
            value={copySourceId}
            options={copySourceOptions}
            onChange={(e) => setCopySourceId(e.value)}
            placeholder={
              isContractListLoading ? "Loading…" : "Select a source contract"
            }
            filter
            filterBy="label"
            style={{ width: "100%" }}
            emptyMessage="No other contracts found"
          />
        </Dialog>
      )}
      {rules.length > 0 ? (
        <StyledDataTable
          value={rules}
          dataKey="rowKey"
          responsiveLayout="scroll"
          className="p-datatable-sm"
          scrollable
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
          rowExpansionTemplate={rowExpansionTemplate}
          rowClassName={(row: IAdjustmentRule) =>
            row.disabledAt != null ? "adjustment-rule-row--disabled" : ""
          }
        >
          <Column
            expander={(row: IAdjustmentRule) => row.hasLanguageContent}
            headerStyle={{ width: "2.5rem", flex: "0 0 2.5rem" }}
            bodyStyle={{ width: "2.5rem", flex: "0 0 2.5rem" }}
          />
          <Column
            field="ruleType"
            header="Rule Type"
            body={ruleTypeTemplate}
            style={{ minWidth: "10rem" }}
          />
          {/* Hospital staff get a read-only view — no enable/disable switch. */}
          {!hospitalId && (
            <Column
              field="status"
              header="Status"
              bodyClassName="rule-status-cell"
              headerStyle={{ width: "6rem", flex: "0 0 6rem" }}
              bodyStyle={{ width: "6rem", flex: "0 0 6rem" }}
              body={(row: IAdjustmentRule) => ruleStatusToggle(row)}
            />
          )}
          <Column
            field="ruleName"
            header="Rule Name"
            body={ruleNameTemplate}
            style={{ minWidth: "12rem" }}
          />
          <Column
            field="providerName"
            header="Provider Name"
            style={{ minWidth: "10rem" }}
          />
          <Column
            header="Discounts"
            body={discountsTemplate}
            style={{ minWidth: "12rem" }}
          />
          <Column
            header="CPT/DRG Codes"
            body={(row: IAdjustmentRule) =>
              codesTemplate(row.cptCodes, "No codes configured")
            }
            style={{ minWidth: "8rem" }}
          />
          <Column
            header="Excluded CPT Codes"
            body={(row: IAdjustmentRule) =>
              codesTemplate(row.excludedCptCodes, "—")
            }
            style={{ minWidth: "8rem" }}
          />
          <Column
            header="Additional Info"
            body={additionalInfoTemplate}
            style={{ minWidth: "14rem" }}
          />
          {/* Hospital staff get a read-only view — no per-row delete. */}
          {!hospitalId && (
            <Column
              headerStyle={{ width: "3.5rem", flex: "0 0 3.5rem" }}
              bodyStyle={{ width: "3.5rem", flex: "0 0 3.5rem" }}
              body={(row: IAdjustmentRule) => ruleDeleteButton(row)}
            />
          )}
        </StyledDataTable>
      ) : (
        <div className="text-center p-4 text-600">
          No adjustment rules to display yet.
        </div>
      )}
    </Card>
  );
}
